import React from "react";
import { useDevicePixelSnap } from "../../../hooks/use_device_pixel_snap";

// The widest the panel may be dragged; it opens at the prototype's 248px.
export const DESKTOP_SIDEBAR_EXPANDED_WIDTH = 272;
export const DESKTOP_SIDEBAR_DEFAULT_WIDTH = 248;
// Width of the collapsed icon rail: a 38px item plus 5px padding on each side, as
// in the designer prototype (its sidebar is 48px when collapsed).
export const DESKTOP_SIDEBAR_COLLAPSED_WIDTH = 48;

// The prototype's easing for the sidebar's width; the page's left inset moves on the
// same curve so the two stay glued together while the sidebar opens or closes.
export const DESKTOP_SIDEBAR_EASE = "220ms cubic-bezier(.2,.7,.2,1)";

const OPEN_WIDTH = `var(--app-sidebar-width, ${DESKTOP_SIDEBAR_DEFAULT_WIDTH}px)`;

// One sidebar for every state: its width animates between the rail and the panel, and
// while peeking it grows over the page — the page's inset stays at the rail width.
export const DesktopSidebarWrapper = ({ isCollapsed, isPeeking, children, ...props }) => (
	<aside
		className={[
			"app-sidebar-aside fixed top-0 left-0 bottom-0 z-[10001] flex overflow-hidden",
			"bg-bg-page border-r border-r-[#dedcf1] dark:bg-dark-base dark:border-r-[rgba(255,255,255,0.07)]",
			isPeeking
				? "[box-shadow:4px_0_24px_rgba(0,0,0,0.14)] dark:[box-shadow:4px_0_24px_rgba(0,0,0,0.5)]"
				: "[box-shadow:4px_0_16px_rgba(0,0,0,0.06)] dark:[box-shadow:4px_0_16px_rgba(0,0,0,0.35)]",
		].join(" ")}
		style={{
			width: isCollapsed && !isPeeking ? `${DESKTOP_SIDEBAR_COLLAPSED_WIDTH}px` : OPEN_WIDTH,
			transition: `width ${DESKTOP_SIDEBAR_EASE}, box-shadow 220ms ease`,
		}}
		{...props}
	>
		{children}
	</aside>
);

// Laid out at the final width from the first frame, so the growing aside only reveals
// it: labels don't wrap and the container queries don't flip mid-animation. The 1px
// taken off is the aside's own border.
export const DesktopSidebarContent = ({ $open, children, ...props }) => (
	<div
		className="@container relative flex min-h-0 shrink-0 flex-col select-none"
		style={{
			width: $open ? `calc(${OPEN_WIDTH} - 1px)` : `${DESKTOP_SIDEBAR_COLLAPSED_WIDTH - 1}px`,
		}}
		{...props}
	>
		{children}
	</div>
);

export const DesktopSidebarTopBar = ({ $open, children, ...props }) => (
	<div
		// A 7px left inset in both states, not 12px like the nav below: it centres the emblem
		// on the rail and keeps it on that spot inside the open logo.
		className={[
			"shrink-0 flex items-center",
			$open ? "justify-between p-[20px_12px_0_7px]" : "pt-[20px] pl-[7px]",
		].join(" ")}
		{...props}
	>
		{children}
	</div>
);

export const DesktopSidebarScrollable = ({ $open, children, ...props }) => (
	<div
		className={[
			// 16px under the logo, as the prototype leaves under its mark; the same in both states,
			// so the icons do not hop up or down when the panel opens.
			"sidebar-scrollable mt-[16px] flex-[1_1_auto] min-h-0 overflow-y-auto overflow-x-hidden pb-[16px]",
			$open
				? "[scrollbar-width:thin] [scrollbar-color:rgba(114,95,237,0.3)_transparent]"
				: "flex flex-col items-center [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
		].join(" ")}
		{...props}
	>
		{children}
	</div>
);

const PANEL_GROUP_CLASS = "flex flex-col px-[12px] @max-[210px]:px-[10px] @max-[176px]:px-[7px]";

export const DesktopSidebarNav = ({ $open, children, ...props }) => (
	<nav className={$open ? PANEL_GROUP_CLASS : "flex flex-col items-center"} {...props}>
		{children}
	</nav>
);

export const DesktopSidebarSection = ({ $open, children, ...props }) => (
	<div className={$open ? PANEL_GROUP_CLASS : "flex flex-col items-center"} {...props}>
		{children}
	</div>
);

// On the rail a section's heading shrinks to a hairline between icon groups. As in the prototype
// it is 4px short of the icons on each side and as light as the sidebar's own border.
const RAIL_HAIRLINE_CLASS = "h-px shrink-0 bg-[#dedcf1] dark:bg-[rgba(255,255,255,0.12)]";

export const DesktopSidebarRailDivider = () => (
	<div className={`mt-[10px] mb-[8px] w-[30px] ${RAIL_HAIRLINE_CLASS}`} />
);

export const DesktopSidebarSectionHeader = ({ children, ...props }) => (
	<button
		// The prototype's heading: a quiet caption with no hover fill, 16px above and 6px below.
		className="flex justify-between items-center w-full p-[16px_10px_6px] cursor-pointer"
		{...props}
	>
		{children}
	</button>
);

export const DesktopSidebarSectionTitle = ({ children, ...props }) => (
	<span
		className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-nav-inactive font-[Inter] text-[12px] font-normal leading-[19px] tracking-[.04em] uppercase dark:text-text-dark-muted"
		{...props}
	>
		{children}
	</span>
);

export const DesktopSidebarSectionAction = ({ $isExpanded, children, ...props }) => (
	<div
		className="inline-flex items-center justify-center shrink-0 w-[14px] h-[14px] text-nav-inactive dark:text-text-dark-muted [&_svg]:w-[14px] [&_svg]:h-[14px]"
		{...props}
	>
		<div
			style={{
				display: "flex",
				transition: "transform 0.2s",
				transform: $isExpanded ? "rotate(180deg)" : "rotate(0deg)",
			}}
		>
			{children}
		</div>
	</div>
);

// Rows sit flush, as in the prototype. The hairline above them is ours: without it the
// admin icons ran straight into these on the rail.
export const DesktopSidebarBottom = ({ $open, children, ...props }) => {
	// Anchored to the window's bottom edge, this hairline would blur at fractional zoom.
	const hairlineRef = useDevicePixelSnap();
	return (
		<div
			className={["flex shrink-0 flex-col pb-[14px]", $open ? "px-[12px]" : "items-center"].join(
				" ",
			)}
			{...props}
		>
			{/* 4px short of the rows on each side, like the rail's 30px hairline under 38px icons. */}
			<div
				ref={hairlineRef}
				className={["mb-[8px]", RAIL_HAIRLINE_CLASS, $open ? "mx-[4px]" : "w-[30px]"].join(" ")}
			/>
			{children}
		</div>
	);
};

export const DesktopCollapseButton = ({ children, ...props }) => (
	<button
		type="button"
		className="inline-flex shrink-0 items-center justify-center w-[32px] h-[32px] rounded-[8px] text-text-secondary transition-[background_0.2s] hover:bg-primary/15 [&_svg]:w-[20px] [&_svg]:h-[20px]"
		{...props}
	>
		{children}
	</button>
);

export const HeaderLogoContainer = ({ children, ...props }) => (
	<div
		className="max-lg:absolute max-lg:top-1/2 max-lg:left-[30px] max-lg:-translate-y-1/2 max-[900px]:left-[20px] max-[530px]:hidden [&_svg]:max-lg:w-[105px] [&_svg]:max-[900px]:w-[100px] [&_svg]:max-[600px]:w-[85px]"
		{...props}
	>
		{children}
	</div>
);

export const MainNavContainer = ({ children, ...props }) => (
	<div
		// `contents` on the phone: the More button is a sibling of this box, not a child,
		// so as long as this box lays out its own items they share its width between them
		// and the More button is left with whatever is beyond its right edge — jammed
		// against the screen while every other item sits in a wide slot. Dropping the box
		// out of the layout makes all five equal children of the bar itself.
		className="flex items-center gap-[13px] mr-[60px] max-lg:mr-0 max-xs:contents"
		{...props}
	>
		{children}
	</div>
);

export const AppHeaderContainer = ({ children, ...props }) => (
	<header
		// Heights: 56 desktop, 70 on the tablet bottom bar, 64 on the phone's. The phone
		// row is the tighter one — icon over a 10.5px label needs 46px, and the prototype
		// gives it 55. Changing any of these means changing --app-header-h-expanded in
		// app-layout.css and the More menu's offsets with it; see header_more_menu.
		// The phone bar keeps only the prototype's own 4px at the sides (its bottom nav is
		// `padding: 4px 4px ...`): the items divide the full width between them, and the
		// outermost two are held off the screen edge without losing a slot's worth of room
		// to padding, as they did at 34px.
		className="app-header-container relative z-[1000] flex justify-between items-center px-[48px] h-[56px] bg-white dark:bg-dark-base [box-shadow:0_4px_4px_rgba(0,0,0,0.1)] select-none max-lg:h-[70px] max-lg:pb-[5px] max-lg:justify-center max-lg:[box-shadow:2px_4px_20px_0px_rgba(0,0,0,0.25)] max-xs:h-[64px] max-xs:px-[4px] max-xs:pb-[4px] max-[412px]:[box-shadow:0_2px_20px_0px_rgba(0,0,0,0.15)]"
		{...props}
	>
		{children}
	</header>
);

export const HideHeaderButtonWrapper = ({ isAppMenuExpanded, children, ...props }) => (
	<button
		className="hide-header-btn-wrapper"
		data-collapsed={isAppMenuExpanded ? "false" : "true"}
		{...props}
	>
		{children}
	</button>
);

export const HideHeaderButton = ({ isAppMenuExpanded, children, ...props }) => (
	<div className="hide-header-btn" data-collapsed={isAppMenuExpanded ? "false" : "true"} {...props}>
		{children}
	</div>
);

const RESIZE_HANDLE_CLASS = [
	"app-sidebar-handle fixed top-0 bottom-0 z-[10002] w-[8px] cursor-col-resize select-none",
	"[border-left:2px_solid_transparent] transition-[border-color]",
	"hover:[border-left-color:rgba(114,95,237,0.4)]",
].join(" ");

// Starts on the sidebar's 1px border and reaches 7px over the page, rather than sitting inside, where
// it covered the panel's scrollbar. Its left follows the sidebar's width on the same curve.
export const DesktopSidebarResizeHandle = ({ isCollapsed, onPointerDown, onDoubleClick }) => (
	<div
		role="separator"
		aria-orientation="vertical"
		data-testid="sidebar-resize-handle"
		onDoubleClick={onDoubleClick}
		onPointerDown={onPointerDown}
		className={RESIZE_HANDLE_CLASS}
		style={{
			left: `calc(${isCollapsed ? `${DESKTOP_SIDEBAR_COLLAPSED_WIDTH}px` : OPEN_WIDTH} - 1px)`,
			transition: `left ${DESKTOP_SIDEBAR_EASE}, border-color 150ms ease`,
		}}
	/>
);
